"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { createNote } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import ImageUploader from '@/components/ImageUploader';
import ProductImageManager from '@/components/ProductImageManager';
import RichTextEditor from '@/components/RichTextEditor';
import LocationInput, { NoteLocation } from '@/components/LocationInput';
import LocationSelectorModal from '@/components/LocationSelectorModal';
import CategorySelector from '@/components/CategorySelector';

interface MultiStepNoteFormProps {
  session: Session | null;
  profile?: any;
  // When editing, provide initialValues (can include `id`)
  initialValues?: {
    id?: string;
    title?: string;
    content?: string;
    photoUrl?: string | null;
    location?: NoteLocation | null;
    categoryId?: string | null;
  } | null;
  // Render all steps in a single view instead of a wizard
  singleView?: boolean;
  // Optional cancel callback for singleView (e.g. navigate back)
  onCancel?: () => void;
  // Optional initial category when creating
  initialCategoryId?: string | null;
  // Called after successful create/update
  onSuccess?: (noteId?: string) => void;
  // If provided, called to perform save (update). If not provided, createNote is used.
  onSubmit?: (payload: {
    title: string;
    content: string;
    photoUrl: string | null;
    imageUrls?: string[] | null;
    locationDisplayName: string | null;
    latitude: number | null;
    longitude: number | null;
    categoryId: string | null;
  }) => Promise<void>;
}

const DRAFT_STORAGE_KEY = 'unsaved-note-draft';

const MultiStepNoteForm: React.FC<MultiStepNoteFormProps> = ({ session, profile, initialValues = null, initialCategoryId = null, onSuccess, onSubmit, singleView = false, onCancel }) => {
  const [index, setIndex] = useState<number>(0);
  // Initialize from initialValues to avoid intermediate empty renders that cause flicker
  const initialTitle = initialValues?.title ?? '';
  const initialContent = initialValues?.content ?? '';
  const initialPhoto = typeof initialValues?.photoUrl !== 'undefined' ? initialValues?.photoUrl ?? null : null;
  const initialImageArray = (Array.isArray((initialValues as any)?.imageUrls) && (initialValues as any).imageUrls.length > 0)
    ? (initialValues as any).imageUrls
    : (Array.isArray((initialValues as any)?.image_urls) && (initialValues as any).image_urls.length > 0)
      ? (initialValues as any).image_urls
      : (initialPhoto ? [initialPhoto] : []);

  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [photoUrl, setPhotoUrl] = useState<string | null>(initialPhoto);
  const [imageUrls, setImageUrls] = useState<string[]>(initialImageArray);
  const [initialImagesSynced, setInitialImagesSynced] = useState(initialImageArray.length > 0);
  const [location, setLocation] = useState<NoteLocation | null>(initialValues?.location ?? null);
  const [showLocationEditor, setShowLocationEditor] = useState(false);
  const [locationEditorAutoFocus, setLocationEditorAutoFocus] = useState(false);
  const [editingLatitude, setEditingLatitude] = useState<number | null>(null);
  const [editingLongitude, setEditingLongitude] = useState<number | null>(null);
  const [editingLocationName, setEditingLocationName] = useState<string | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(initialCategoryId);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Determine storage key: use note-specific key when editing to avoid clobbering new-note drafts
  const storageKey = (initialValues && initialValues.id) ? `unsaved-note-draft-${initialValues.id}` : DRAFT_STORAGE_KEY;

  const visibleSteps = [
    { id: 1, label: 'Título', target: 1 },
    { id: 2, label: 'Contenido', target: 2 },
    { id: 3, label: 'Detalles', target: 3 },
  ];
  const maxIndex = visibleSteps.length - 1;
  const next = () => setIndex((i) => Math.min(maxIndex, i + 1));
  const prev = () => setIndex((i) => Math.max(0, i - 1));

  // Load draft when creating a new note (no initialValues). When editing (initialValues present) we already
  // initialized state synchronously to avoid intermediate empty renders that cause flicker.
  useEffect(() => {
    if (initialValues) {
      // Already initialized from props; skip draft restoration.
      return;
    }

    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw || '{}');
        setTitle(parsed.title || '');
        setContent(parsed.content || '');
        setPhotoUrl(parsed.photoUrl || null);
        setImageUrls(parsed.photoUrl ? [parsed.photoUrl] : []);
        setLocation(parsed.location || null);
        setSelectedCategoryId(parsed.selectedCategoryId || initialCategoryId || null);
      } else {
        if (initialCategoryId) setSelectedCategoryId(initialCategoryId);

        try {
          // initialize location from profile when creating new note
          // @ts-ignore
          if ((typeof profile !== 'undefined') && profile) {
            // @ts-ignore
            const lat = profile.relevant_latitude ?? profile.latitude ?? null;
            // @ts-ignore
            const lon = profile.relevant_longitude ?? profile.longitude ?? null;
            // @ts-ignore
            const name = profile.relevant_location_display_name ?? profile.location_name ?? null;

            if (lat !== null && lon !== null) {
              setLocation({ displayName: name || `Lat: ${lat.toFixed(4)}, Lon: ${lon.toFixed(4)}`, latitude: lat, longitude: lon } as NoteLocation);
            } else if (name) {
              setLocation({ displayName: name, latitude: null as any, longitude: null as any } as NoteLocation);
            }
          }
        } catch (e) {
          // ignore malformed profile
        }
      }
    } catch (err) {
      // ignore
    }
  }, [initialCategoryId, profile, storageKey, initialValues]);

  // Keep imageUrls in sync with a single photoUrl when the user hasn't interacted with images yet
  useEffect(() => {
    if (!initialImagesSynced) return;
    // do not overwrite if user already added/removed images
    if (imageUrls.length === 0 && photoUrl) {
      setImageUrls([photoUrl]);
    }
  }, [photoUrl, initialImagesSynced]);

  // Autosave draft
  useEffect(() => {
    // Only autosave when creating or when editing with an id-specific storageKey
    const toSave = { title, content, photoUrl, location, selectedCategoryId };
    try {
      localStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch {}
  }, [title, content, photoUrl, location, selectedCategoryId, storageKey]);

  const handlePublish = async () => {
    if (!session?.user?.id) {
      toast({ title: 'Error', description: 'Debes iniciar sesión para publicar.', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      if (typeof onSubmit === 'function') {
        await onSubmit({
          title,
          content,
          photoUrl,
          imageUrls,
          locationDisplayName: location?.displayName || null,
          latitude: location?.latitude || null,
          longitude: location?.longitude || null,
          categoryId: selectedCategoryId,
        });
      } else {
        await createNote(
          session.user.id,
          title,
          content,
          photoUrl,
          imageUrls.length > 0 ? imageUrls : null,
          location?.displayName || null,
          location?.latitude || null,
          location?.longitude || null,
          selectedCategoryId
        );
      }

      const isEdit = !!(initialValues && initialValues.id);
      toast({ title: isEdit ? 'Nota actualizada' : 'Nota publicada', description: isEdit ? 'Tu nota ha sido actualizada correctamente.' : 'Tu nota ha sido publicada exitosamente.' });
      try { localStorage.removeItem(storageKey); } catch {}
      if (onSuccess) onSuccess(initialValues?.id);
    } catch (err) {
      console.error('Error publishing note', err);
      toast({ title: 'Error', description: 'No se pudo publicar la nota.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="rounded-lg border bg-card text-card-foreground shadow-sm">
      {/* Stepper visual */}
      {!singleView && (
        <div className="p-4 border-b border-primary/30 bg-card">
          <ol className="flex items-center justify-between gap-4 max-w-full mx-auto">
            {visibleSteps.map((s, i) => {
              const active = i === index;
              const done = i < index;
              return (
                <li key={s.id} className="flex-1">
                  <button type="button" onClick={() => setIndex(i)} className="w-full text-left">
                    <div className="flex items-center gap-3">
                      <div className={`flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium ring-offset-background ${active ? 'bg-primary text-white' : done ? 'bg-primary/20 text-primary' : 'border bg-transparent text-muted-foreground'}`}>
                        {i + 1}
                      </div>
                      <div className={`text-sm ${active ? 'font-semibold text-foreground' : 'sr-only'}`}>{s.label}</div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      <CardContent className="p-6">
        {(singleView || index === 0) && (
          <div className="space-y-4">
            <Label htmlFor="note-title">Título</Label>
            <Input id="note-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título para tu nota" />
          </div>
        )}

        {(singleView || index === 1) && (
          <div className="space-y-4">
            <Label>Contenido</Label>
            <RichTextEditor content={content} onChange={setContent} placeholder="Escribe el contenido..." />
            <ProductImageManager
              session={session}
              initialImageUrls={imageUrls}
              initialMainImageUrl={imageUrls[0] || null}
              onImagesChange={(allImageUrls) => {
                // onImagesChange signature: (allImageUrls, mainImageUrl, imagesToDelete)
                const imgs = Array.isArray(allImageUrls) ? allImageUrls : [];
                setImageUrls(imgs);
                setPhotoUrl(imgs.length > 0 ? imgs[0] : null);
              }}
              disabled={loading}
            />
            {/* Navigation buttons are provided in the footer stepper; removed inner buttons to avoid duplication */}
          </div>
        )}

        {(singleView || index === 2) && (
          <div className="space-y-4">
            <CategorySelector selectedCategoryId={selectedCategoryId} onCategoryChange={setSelectedCategoryId} />
            <div className="grid gap-2 p-3 bg-muted rounded-md">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Label className="text-sm font-medium">Ubicación de la nota</Label>
                  {!showLocationEditor ? (
                        <button
                          type="button"
                          className="w-full text-left text-sm text-muted-foreground mt-1 px-2 py-1 border border-transparent rounded-md hover:bg-muted"
                          onClick={() => {
                            setShowLocationModal(true);
                          }}
                        >
                          {(() => {
                            const effectiveName = location?.displayName || null;
                            if (effectiveName) {
                              const idx = effectiveName.indexOf(',');
                              return (idx === -1 ? effectiveName : effectiveName.slice(0, idx)) + (" ") as any;
                            }

                            return 'Ubicación no establecida';
                          })()}
                        </button>
                      ) : null}
                </div>
                <div className="ml-2">
                  {!showLocationEditor ? (
                        <Button size="sm" variant="ghost" onClick={() => { setShowLocationModal(true); }}>
                          Editar
                        </Button>
                      ) : (
                        <div className="flex space-x-2">
                          <Button size="sm" variant="ghost" onClick={() => { setShowLocationModal(false); setLocationEditorAutoFocus(false); }}>Cerrar</Button>
                        </div>
                      )}
                </div>
              </div>

              {/* Use modal for location editing */}
              <LocationSelectorModal
                isOpen={showLocationModal}
                onClose={() => setShowLocationModal(false)}
                initial={location}
                onSave={async (loc) => {
                  setLocation(loc);
                  setShowLocationModal(false);
                }}
              />
            </div>
            {/* Use footer controls for navigation and publishing */}
          </div>
        )}
      </CardContent>

        {singleView ? (
          <CardFooter className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {onCancel && <Button variant="outline" onClick={onCancel} disabled={loading}>Cancelar</Button>}
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handlePublish} disabled={loading}>{loading ? (initialValues ? 'Guardando...' : 'Publicando...') : (initialValues ? 'Guardar' : 'Publicar')}</Button>
            </div>
          </CardFooter>
        ) : (
          <CardFooter className="flex items-center justify-between">
            <div className="text-sm">Paso {index + 1} de {visibleSteps.length}</div>
            <div className="flex items-center gap-2">
              {index > 0 && (
                <Button variant="ghost" onClick={prev} disabled={loading}>Anterior</Button>
              )}
              {index < maxIndex ? (
                <Button onClick={next} disabled={loading}>Siguiente</Button>
              ) : (
                <Button onClick={handlePublish} disabled={loading}>{loading ? 'Publicando...' : 'Publicar'}</Button>
              )}
            </div>
          </CardFooter>
        )}
    </Card>
  );
};

export default MultiStepNoteForm;
